/* prettier-ignore */

import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { parseDates, areDatesValid } from './chat-actions/helpers/date.utils';
import { getCoordinates } from './chat-actions/helpers/google-location.utils';
import { RestService } from 'src/app/services/rest.service';
import { GenerateRouteService } from './chat-actions/generate-route.service';
import { RegisterFormService } from './chat-actions/register-form.service';
import { ShowItemDetailsService } from './chat-actions/show-item-details.service';
import { ShowComponentService } from './chat-actions/show-component.service';
import { RemoveItemFromCustomRouteService } from './chat-actions/remove-item-from-custom-route.service';
import { AddItemToCustomRouteService } from './chat-actions/add-item-to-custom-route.service';
// import { SendCurrentRouteService } from './chat-actions/send-current-route.service';
// import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { isNotSuccess, validateRoute } from './chat-actions/guards/custom-route.guard';
// import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { GetFreeSlotsService } from './chat-actions/get-free-slots.service';
import { from, mapTo, Observable, of, take, tap } from 'rxjs';
import { DataChatService } from './dataChat.service';
import { ScrollService } from './scrollContainer.service';

@Injectable({ providedIn: 'root' })

export class ChatActionHandlerService {
    private _sessionStorageService = inject(SessionStorageService);
    private _destroyRef = inject(DestroyRef);
    private _generateRouteService = inject(GenerateRouteService);
    private _registerFormService = inject(RegisterFormService);
    private _showItemDetails = inject(ShowItemDetailsService);
    private _showComponentService = inject(ShowComponentService);
    private _removeItemFromCustomRouteService = inject(RemoveItemFromCustomRouteService);
    private _addItemToCustomRouteService = inject(AddItemToCustomRouteService);
    // private _sendCurrentRouteService = inject(SendCurrentRouteService)
    // private _customRouteService = inject(CustomRouteService);
    // private _plannerDayStateService = inject(PlannerDayStateService);
    private _getFreeSlotsService = inject(GetFreeSlotsService)
    private _dataChat = inject(DataChatService);
    private _restService = inject(RestService);

    public openAIFeedback = signal(null);
    private generatingRoute = signal(false);
    public scrollService = inject(ScrollService);

    detectFormChanges = effect(() => {
        const form = this._registerFormService.signalForm();
        console.log("TOOLS:: Form values changed:", form);
        if (!this._registerFormService.isValidForm()) {
            const invalids = this._registerFormService.getInvalidControls();
            console.warn('TOOLS: Form invalid:', invalids);
            return;
        }
        
        console.log("TOOLS:: Form is valid, ready to generate route. Current values:", form);

        if (this.generatingRoute())return;

        console.log("TOOLS:: SENDING ROUTE");
        this.generatingRoute.set(true);
        this.startRoute();
    }, { allowSignalWrites: true })

    public actionFromChatBot(result): Observable<void> {
        console.log("TOOLS::", result);
        switch (result?.action) {
        
            // CamelCase Mappings (Backend)
            case 'CreateTravelDates': return this.setTravelDates(result);
            case 'CreateTravelGroup': return this.setTravelGroupType(result);
            case 'CreateTravelInterests': return this.setTravelInterests(result);
            case 'CreateTransportPreferences': return this.setTransportPreference(result);
            case 'CreateDistancePreferences': return this.setDistancePreference(result);
            case 'CreateTravelTimes': return this.setTravelTimes(result);
            case 'CreateStartingPoint': return this.createStartingPoint(result);
            case 'CheckValidForm': return this.checkValidForm(result);
            case 'ShowItemDetails': return this.showItemDetails(result);
            case 'ShowTimelineOptions': return this.showTimeLineOptions(result);
            case 'AddPointToItinerary': return this.addItem(result);
            case 'RemovePointFromItinerary': return this.removeItem(result); // Guessing name
            case 'ShowMap': return this.showMapView(result);

            // Legacy / snak_case mappings
            case 'create_travel_dates': return this.setTravelDates(result);
            case 'create_travel_group_type': return this.setTravelGroupType(result);
            case 'create_travel_interests': return this.setTravelInterests(result);
            case 'create_transport_preferences': return this.setTransportPreference(result);
            case 'create_distance_preferences': return this.setDistancePreference(result);
            case 'request_slot_candidates': return this.showTimeLineOptions(result);
            case 'show_timeline_options': return this.showTimeLineOptions(result);
            case 'show_point_details': return this.showItemDetails(result);
            case 'add_point_to_itinerary': return this.addItem(result);
            case 'remove_point_from_itinerary': return this.removeItem(result);
            case 'change_language': return this.changeLanguage(result);
            case 'return_to_timeline': return this.showTimeLineView(result);
            case 'return_to_map': return this.showMapView(result);
            case 'go_to_mail_form': return this.showSendRouteView(result);
            case 'get_current_itinerary': return this.sendCurrentRoute(result);
            case 'show_route_day': return this.showRouteDay(result);
            case 'create_starting_point': return this.createStartingPoint(result);
            case 'modify_timeline_slot': return this.modifyTimelineSlot(result);
            case 'check_valid_form': return this.checkValidForm(result);
            case 'update_interests': return this.updateInterests(result);
            default:
                console.warn("TOOLS:: Unknown action:", result?.action);
                return of(void 0);
        }
    }

    private generateInitialPayload(result: any) {
        return {
            action: result.action,
            content: null,
            is_successful: false,
            operation_id: result.operation_id,
            completion_id: result.completion_id,
        }
    }

    private changeLanguage(item): Observable<void> {
        console.warn("change_language tool not implemented");
        const feedBack = this.generateInitialPayload(item);
        this.openAIFeedback.set(feedBack);
        return of(void 0);
    }

    //---------- IA REGISTER USER TO GENERATE ROUTE ----------------------------------------
    private setTravelDates(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const dates = parseDates(item.start_date, item.end_date);
        if (!areDatesValid(dates)) {
            this.openAIFeedback.set({ ...feedBack, reason: 'invalid dates' });
            return of(void 0);
        }

        this._registerFormService.setDateRange(item);
        this.openAIFeedback.set({ ...feedBack, is_successful: true });
        return of(void 0);
    }

    private setTravelGroupType(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setTripType(item.group_id);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTravelInterests(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setInterestIdList(item.interest_ids)
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTransportPreference(item): Observable<void> {
        // const feedBack = this.generateInitialPayload(item);
        // const res = this._registerFormService.setTransportMeans(item.transport_methods);
        // this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setDistancePreference(item): Observable<void> {
        // const feedBack = this.generateInitialPayload(item);
        // const res = this._registerFormService.setDistancePreferences(item.max_distance_meters);
        // this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTravelTimes(item): Observable<void> {
        // const feedBack = this.generateInitialPayload(item);
        // const res = this._registerFormService.setTimes({
        //     startTime: item.start_time,
        //     endTime: item.end_time
        // });
        // this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private createStartingPoint(args: any): Observable<void> {
        return;
        // const feedBack = this.generateInitialPayload(args);
        // // Priorizar la ciudad configurada en el dispositivo (restService)
        // const city = this._restService?.device?.location?.city ||
        //     this._restService?.device?.location?.address ||
        //     this._restService?.subDomain || null;

        // console.log(`TOOLS: Geocoding '${args?.starting_point}' with context city: ${city}`);

        // return from(getCoordinates(args?.starting_point, city)).pipe(
        //     tap(startLocation => {
        //         if (!startLocation) {
        //             this.openAIFeedback.set({
        //                 ...feedBack,
        //                 is_successful: false,
        //                 reason: 'No se ha podido establecer el punto de partida'
        //             });
        //             return;
        //         }
        //         const address = {
        //             ...startLocation,
        //             title: args?.starting_point
        //         };
        //         console.log("TOOLS:: Geocoding result:", address);
        //         const res = this._registerFormService.setStartLocation(address);
        //         this.openAIFeedback.set({ ...feedBack, ...res });
        //     }),
        //     take(1),
        //     mapTo(void 0)
        // );
    }

    private startRoute(): void {
        return;
        // const subdomain = this._restService?.subDomain || 'emt';
        // const values = this._registerFormService.getFormValues();
        // // Forzar explícitamente en el objeto final antes de enviar si por alguna razón el form no sincronizó
        // values.subdomain = subdomain;

        // this._generateRouteService.sendData(values, this._destroyRef, '/custom-route')
        //     .subscribe({
        //         next: (result) => {
        //             console.log('TOOLS: Route generated successfully', result);
        //             this._registerFormService.resetForm();
        //             //TODO: check this
        //             this.generatingRoute.set(false);
        //             console.log("Starting Route & Reseting Form");
        //         },
        //         error: err => {
        //             console.error('TOOLS: Error registering user:', err);
        //             this._registerFormService.resetForm();
        //             this.generatingRoute.set(false);
        //         }
        //     });
    }
    //---------- IA CHATBOT CUSTOM ROUTE FUNCTIONS ----------------------------------------
    private showItemDetails(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);

        // Si no tiene element_id pero tiene found_name, resolver desde la ruta actual
        if (!item?.element_id && item?.found_name) {
            const resolved = this._resolvePointByName(item.found_name);
            if (resolved) {
                item = { ...item, element_id: String(resolved.id), type: resolved.type || 'point' };
            } else {
                this.openAIFeedback.set({ ...feedBack, is_successful: false, reason: 'No se encontró el punto en la ruta' });
                return of(void 0);
            }
        }

        return this._showItemDetails.show(item).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private addItem(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._addItemToCustomRouteService.addItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private removeItem(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        return this._removeItemFromCustomRouteService.removeItem(args).pipe(
            take(1),
            tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
            mapTo(void 0)
        );
    }

    private showTimeLineView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showMapView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showMapComponent();
        
        console.log("TOOLS:: showMapView result:", res);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showSendRouteView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showSendRouteComponent();
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showTimeLineOptions(args): Observable<void> {
        return of(void 0);
        // const feedBack = this.generateInitialPayload(args);
        // const resolved = this._resolveSlotFromArgs(args);
        // if (!resolved) {
        //     this.openAIFeedback.set({ ...feedBack, is_successful: false, reason: 'No se encontró un slot disponible' });
        //     return of(void 0);
        // }
        // return this._getFreeSlotsService.getAvailableItemsOnSlot(resolved.dayIndex, resolved.hour).pipe(
        //     take(1),
        //     tap((res: any) => {
        //         if (!res?.is_successful) {
        //             this.openAIFeedback.set({ ...feedBack, res });
        //             return;
        //         }

        //         let parsedResponse = JSON.parse(res?.content)
        //         this.openAIFeedback.set({
        //             ...feedBack,
        //             content: parsedResponse,
        //             'is_successful': true,
        //         })
        //     }),
        //     mapTo(void 0)
        // );
    }

    private sendCurrentRoute(args): Observable<void> {
        // const feedBack = this.generateInitialPayload(args);
        // const res = this._sendCurrentRouteService.getRoute();
        
        // console.log("TOOLS:: sendCurrentRoute result:", res);
        // this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showRouteDay(args): Observable<void> {
        // const feedBack = this.generateInitialPayload(args);
        // const route = this._customRouteService.customRouteData();
        // const token = this._customRouteService.token();

        // const ctx = validateRoute(route, token, args?.day);
        // if (isNotSuccess(ctx)) {
        //     this.openAIFeedback.set({ ...feedBack, ctx });
        //     return of(void 0);
        // }

        // const dayIndex = Number(args?.day - 1);
        // this._plannerDayStateService.setDayToShowIndex(dayIndex);
        // this._showComponentService.showTimelineComponent();
        // this.openAIFeedback.set({ ...feedBack, is_successful: true });
        return of(void 0);
    }

    private modifyTimelineSlot(args): Observable<void> {
        // Redirigir a showTimeLineOptions para mostrar alternativas
        return this.showTimeLineOptions(args);
    }

    /** Normaliza texto: minúsculas + sin acentos */
    private _normalize(text: string): string {
        return (text || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    /** Busca un punto en la ruta actual por nombre (fuzzy match, sin acentos) */
    private _resolvePointByName(name: string): any | null {
        return;
        // const route = this._customRouteService.customRouteData();
        // if (!route?.dailyRoutes) return null;

        // const nameNorm = this._normalize(name);
        // for (const dailyRoute of route.dailyRoutes) {
        //     if (!dailyRoute?.points) continue;
        //     for (const point of dailyRoute.points) {
        //         const title = this._normalize(point.title);
        //         if (title === nameNorm || title.includes(nameNorm) || nameNorm.includes(title)) {
        //             return point;
        //         }
        //     }
        // }
        // return null;
    }

    /**
     * Resuelve dayIndex + hour para show_timeline_options.
     * Prioridad: hour explícito > found_name (buscar punto → slot más cercano) > fallback _lastRemovedPoint > primer slot libre.
     */
    private _resolveSlotFromArgs(args: any): { dayIndex: number; hour: string } | null {
        return;
        // const route = this._customRouteService.customRouteData();
        // if (!route?.dailyRoutes?.length) return null;

        // // 1. Si viene hour explícito, usarlo directamente
        // if (args?.hour) {
        //     const dayIndex = (args?.day ?? 1) - 1;
        //     return { dayIndex, hour: args.hour };
        // }

        // // 2. Si viene found_name, intentar buscarlo
        // let targetPoint = null;
        // let targetDayIndex = -1;

        // if (args?.found_name) {
        //     // A) Buscar en la ruta ACTUAL (por si aún existe)
        //     const nameNorm = this._normalize(args.found_name);
        //     for (let di = 0; di < route.dailyRoutes.length; di++) {
        //         const dailyRoute = route.dailyRoutes[di];
        //         if (!dailyRoute?.points) continue;
        //         for (const point of dailyRoute.points) {
        //             const title = this._normalize(point.title);
        //             if (title.includes(nameNorm) || nameNorm.includes(title)) {
        //                 targetPoint = point;
        //                 targetDayIndex = di;
        //                 break;
        //             }
        //         }
        //         if (targetPoint) break;
        //     }


        // }

        // if (targetPoint && targetPoint.startDate) {
        //     const startDate = targetPoint.startDate;
        //     const d = new Date(startDate);
        //     const hh = String(d.getHours()).padStart(2, '0');
        //     const mm = String(d.getMinutes()).padStart(2, '0');
        //     console.log(`🔍 [_resolveSlotFromArgs] Punto referencia: Día ${targetDayIndex}, ${hh}:${mm}`);

        //     // Buscar el slot libre más cercano a esa hora en ese día
        //     const freeSlots = this._getFreeSlotsService.getFreeSlots(targetDayIndex);
        //     if (freeSlots?.length) {
        //         const targetMin = d.getHours() * 60 + d.getMinutes();
        //         let best = freeSlots[0];
        //         let bestDist = Infinity;

        //         for (const slot of freeSlots) {
        //             const [sh, sm] = slot.startTime.split(':').map(Number);
        //             const dist = Math.abs((sh * 60 + sm) - targetMin);
        //             if (dist < bestDist) {
        //                 bestDist = dist;
        //                 best = slot;
        //             }
        //         }
        //         console.log(`🔍 [_resolveSlotFromArgs] Slot libre más cercano: ${best.startTime} (dist=${bestDist}min)`);
        //         return { dayIndex: targetDayIndex, hour: best.startTime };
        //     }
        // }

        // if (args?.found_name && !targetPoint) {
        //     console.warn(`⚠️ [_resolveSlotFromArgs] Punto "${args.found_name}" no encontrado en la ruta ni en backup`);
        // }

        // // 3. Fallback: primer slot libre del día indicado (o día 0)
        // const dayIndex = (args?.day ?? 1) - 1;
        // const freeSlots = this._getFreeSlotsService.getFreeSlots(dayIndex);
        // if (freeSlots?.length) {
        //     console.log(`🔍 [_resolveSlotFromArgs] Fallback: primer slot libre del día ${dayIndex}: ${freeSlots[0].startTime}`);
        //     return { dayIndex, hour: freeSlots[0].startTime };
        // }

        // console.warn('⚠️ [_resolveSlotFromArgs] No hay slots libres disponibles');
        // return null;
    }

    private checkValidForm(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        if (!this._registerFormService.isValidForm()) {
            const invalids = this._registerFormService.getInvalidControls()?.map(e => e.controlName)?.join(',');
            const res = {
                reason: `Se necesitan los siguientes inputs: ${invalids}`
            };
            this.openAIFeedback.set({ ...feedBack, ...res });
            return of(void 0);
        }

        // Solo disparar si no se está generando ya (detectFormChanges puede haber disparado primero)
        if (this.generatingRoute()) {
            console.log("TOOLS:: CheckValidForm OK but route already generating, skipping duplicate startRoute()");
            const res = { is_successful: true };
            this.openAIFeedback.set({ ...feedBack, ...res });
            return of(void 0);
        }

        console.log("TOOLS:: CheckValidForm OK -> Starting route generation");
        this.generatingRoute.set(true);
        this.startRoute();

        const res = { is_successful: true };
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private updateInterests(result: any): Observable<void> {
        const tags = result?.tags || [];
        console.log("TOOLS:: update_interests tags:", tags);

        const messages = this._dataChat.messages();

        // Buscamos el último mensaje del asistente para ponerle los tags
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'assistant') {
                const updatedMessages = [...messages];
                updatedMessages[i] = { ...updatedMessages[i], tags };
                this._dataChat.messages.set(updatedMessages);
                break;
            }
        }

        setTimeout(() => {
            this.scrollService.scrollToBottom();
        }, 25);

        return of(void 0);
    }

}